import { createId } from "@paralleldrive/cuid2";
import { defaultResumeData, ResumeData } from "@reactive-resume/schema";

import { buildEditableResumeFields } from "@/client/pages/dashboard/ai-optimization/review";

/**
 * Validation and normalization for résumé payloads entering the Coach Resume
 * optimization flow.
 *
 * Extracted from the intake page because these three rules are what stand between a
 * malformed payload and a résumé that either crashes the builder or silently cannot be
 * optimized — both of which have happened. Kept pure so they are testable.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

/**
 * Does this look like a real ResumeData payload?
 *
 * Everything here arrives over the wire — the LMS-built seed, or a résumé fetched by
 * id — so nothing is assumed to be well-formed. An earlier version trusted a payload
 * that turned out to be an error body ({ message, error, statusCode }) and saved it as
 * a résumé's `data`, leaving a résumé with no sections and nothing to optimize.
 */
export const isResumeData = (value: unknown): value is ResumeData => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;

  // An error body has none of these; every real résumé has at least one.
  const hasSections = candidate.sections && typeof candidate.sections === "object";
  const hasBasics = candidate.basics && typeof candidate.basics === "object";

  return Boolean(hasSections || hasBasics);
};

/**
 * Is this résumé worth optimizing?
 *
 * Stricter than isResumeData: it must also contain prose the model can rewrite. A
 * structurally valid but empty résumé yields zero suggestions, so treating it as a
 * usable base just wastes an LLM call and dead-ends the student.
 */
export const isUsableResumeData = (value: unknown) =>
  isResumeData(value) &&
  buildEditableResumeFields(value as unknown as Record<string, unknown>).length > 0;

/**
 * Replace any item id the schema would reject with a fresh cuid2.
 *
 * `idSchema` is `z.string().cuid2()` (zod regex `/^[a-z][a-z0-9]*$/`), and `PATCH
 * /resume/:id` rejects the ENTIRE document if one item id fails. `POST /resume/import`
 * does not validate at all, so a payload with readable ids imports happily and then
 * makes the résumé permanently unsavable — the student reaches the builder and every
 * edit 400s with "Invalid cuid2".
 *
 * Safe to re-id: suggestion `fieldPath`s address items by array index
 * (`sections.experience.items[0].summary`), not by id, so nothing references these.
 */
const withValidItemIds = (sections: Record<string, unknown>): Record<string, unknown> => {
  const repaired: Record<string, unknown> = {};

  for (const [key, section] of Object.entries(sections)) {
    const items = (section as { items?: unknown })?.items;

    if (!Array.isArray(items)) {
      repaired[key] = section;
      continue;
    }

    repaired[key] = {
      ...(section as object),
      items: items.map((item) => {
        const id = (item as { id?: unknown })?.id;
        if (typeof id === "string" && /^[a-z][a-z0-9]*$/.test(id)) return item;

        return { ...(item as object), id: createId() };
      }),
    };
  }

  return repaired;
};

/**
 * Fill in everything the builder expects but the payload omits.
 *
 * `POST /resume/import` binds its body as `any`, so ZodValidationPipe never applies
 * resumeDataSchema and its defaults are NOT filled in server-side — whatever is sent is
 * stored verbatim. A résumé missing `metadata` therefore crashes the builder the moment
 * a panel reads it (NotesSection on `metadata.notes` was the first to go).
 *
 * Merging against Reactive-Resume's own `defaultResumeData` rather than a hand-written
 * list keeps this correct as the schema grows, and guarantees every section the UI
 * iterates exists — including `sections.custom` and `metadata.layout`, which decides
 * what actually renders.
 *
 * Because that same missing validation lets invalid data through, this is also where
 * item ids are repaired — see withValidItemIds.
 */
export const withSchemaDefaults = (partial: unknown): ResumeData => {
  const source = (partial ?? {}) as Record<string, unknown>;

  return {
    ...defaultResumeData,
    ...source,
    basics: { ...defaultResumeData.basics, ...((source.basics ?? {}) as object) },
    sections: withValidItemIds({
      ...defaultResumeData.sections,
      ...((source.sections ?? {}) as object),
    }),
    metadata: { ...defaultResumeData.metadata, ...((source.metadata ?? {}) as object) },
  } as ResumeData;
};
